<template>
  <div>
    <div class="panel">
      <h3>{{ $t('bearings.headlineKeyFigures') }}</h3>
      <div class="table-responsive">
        <table class="table table-striped">
          <tbody>
            <tr>
              <td>
                {{ $t('edgeDistanceEC.effects') }}
                <span class="text-muted">&sigma;<sub>Ed</sub></span>
              </td>
              <td class="text-right">
                {{ compressiveStress | numeralFormat('0.0') }} N/mm²
              </td>
            </tr>
            <tr :class="{ 'table-danger': concretePressure < compressiveStress }">
              <td>
                {{ $t('edgeDistanceEC.concretePressure') }}
                <span class="text-muted">f<sub>cd</sub></span>
              </td>
              <td class="text-right">
                {{ concretePressure | numeralFormat('0.0') }} N/mm²
              </td>
            </tr>
            <tr>
              <td>
                {{ $t('edgeDistanceEC.bearingSqueeze') }}
                <span class="text-muted">&sigma;<sub>Ed</sub> / f<sub>cd</sub></span>
              </td>
              <td class="text-right">
                {{ bearingSqueeze | numeralFormat('0.00') }}
              </td>
            </tr>
            <tr :class="{ 'table-danger': bearingStrength < compressiveStress }">
              <td>
                {{ $t('edgeDistanceEC.bearingStrength') }}
                <span class="text-muted">f<sub>Rd</sub></span>
              </td>
              <td class="text-right">
                {{ bearingStrength | numeralFormat('0.0') }} N/mm²
              </td>
            </tr>
            <tr>
              <td>
                {{ $t('edgeDistanceEC.depthParValue') }} <span class="text-muted">a</span>
              </td>
              <td class="text-right">
                <span v-if="!warningsA2.status && !warningsA3.status">
                  {{ depthParValue | numeralFormat('0.0') }} mm
                </span>
                <span v-else>
                  -
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <span class="text-muted">a<sub>1</sub></span>
              </td>
              <td class="text-right">
                {{ depthBase | numeralFormat('0') }} mm
              </td>
            </tr>
            <tr :class="a2Status">
              <td>
                <span class="text-muted">a<sub>2</sub></span>
              </td>
              <td class="text-right">
                {{ distanceSupporting | numeralFormat('0.00') }} mm

                <font-awesome-icon
                  v-if="warningsA2.status"
                  :id="'warn-a2'"
                  icon="exclamation-triangle"
                  class="text-danger ml-2"
                />

                <b-popover
                  v-if="warningsA2.status"
                  target="warn-a2"
                  triggers="hover"
                  placement="top"
                >
                  <div
                    v-for="item in warningsA2.msg"
                    :key="item"
                  >
                    {{ item }}
                  </div>
                </b-popover>
              </td>
            </tr>
            <tr>
              <td>
                &#916;
                <span class="text-muted">a<sub>2</sub></span>
              </td>
              <td class="text-right">
                {{ maxDistanceSupporting | numeralFormat('0.00') }} mm
              </td>
            </tr>
            <tr>
              <td>
                a<sub>2</sub> + &#916;a<sub>2</sub>
              </td>
              <td class="text-right">
                <span v-if="!warningsA2.status">
                  {{ sumDistanceSupporting | numeralFormat('0.00') }} mm
                </span>
                <span v-else>-</span>
              </td>
            </tr>
            <tr :class="a3Status">
              <td>
                <span class="text-muted">a<sub>3</sub></span>
              </td>
              <td
                class="text-right"
              >
                {{ distanceSupported | numeralFormat('0') }} mm

                <font-awesome-icon
                  v-if="warningsA3.status"
                  :id="'warn-a3'"
                  icon="exclamation-triangle"
                  class="text-danger ml-2"
                />

                <b-popover
                  v-if="warningsA3.status"
                  target="warn-a3"
                  triggers="hover"
                  placement="top"
                >
                  <div
                    v-for="item in warningsA3.msg"
                    :key="item"
                  >
                    {{ item }}
                  </div>
                </b-popover>
              </td>
            </tr>
            <tr>
              <td>
                &#916;
                <span class="text-muted">a<sub>3</sub></span>
                <b-button
                  id="popover-length-max-supported-a3"
                  variant="link"
                  tabindex="-1"
                >
                  <font-awesome-icon icon="question-circle" />
                </b-button>
                <b-popover
                  target="popover-length-max-supported-a3"
                  triggers="hover"
                  placement="top"
                >
                  <p>{{ $t('edgeDistanceEC.lengthMaxSupportedTooltip') }}</p>
                <!--                    <p v-html="$t('general.tooltipAdditionalInfo')"></p>-->
                </b-popover>
              </td>
              <td class="text-right">
                {{ maxDistanceSupported | numeralFormat('0.00') }} mm
              </td>
            </tr>
            <tr>
              <td>
                a<sub>3</sub> + &#916;a<sub>3</sub>
              </td>
              <td class="text-right">
                <span v-if="!warningsA3.status">
                  {{ sumDistanceSupported | numeralFormat('0.00') }} mm
                </span>
                <span v-else>-</span>
              </td>
            </tr>
            <tr>
              <td>
                <strong>
                  {{ $t('edgeDistanceEC.edgeDistance') }}
                </strong>
              </td>
              <td class="text-right">
                <span v-if="!warningsA2.status && !warningsA3.status">
                  <strong>
                    {{ edgeDistance | numeralFormat('0.0') }} mm
                  </strong>
                </span>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="panel has-bg">
      <h3>{{ $t('edgeDistanceEC.headlineIllustration') }}</h3>
      <div class="row">
        <div class="col-md-6">
          <img
            src="../../assets/img/abbildungen/randabstand-EC2-draufsicht.jpg"
            :alt="$t('edgeDistanceEC.headlineIllustration')"
          />
        </div>
        <div class="col-md-6">
          <img
            src="../../assets/img/abbildungen/randabstand-EC2-ansicht.jpg"
            :alt="$t('edgeDistanceEC.headlineIllustration')"
          />
        </div>
      </div>
    </div>
  </div>
<!--  <div class="panel has-bg">-->
<!--    <h3>-->
<!--      a<sub>1</sub>-->
<!--      <b-button-->
<!--        id="popover-depth-supporting-a1"-->
<!--        variant="link"-->
<!--      >-->
<!--        <font-awesome-icon icon="question-circle" />-->
<!--      </b-button>-->
<!--      <b-popover-->
<!--        target="popover-depth-supporting-a1"-->
<!--        triggers="hover"-->
<!--        placement="top"-->
<!--      >-->
<!--        <p>{{ $t('edgeDistanceEC.supportBaseTooltip') }}</p>-->
<!--        &lt;!&ndash;
<p v-html="$t('general.tooltipAdditionalInfo')"></p>&ndash;&gt;-->
<!--      </b-popover>-->
<!--    </h3>-->
<!--    <div class="row">-->
<!--      <div class="col-lg-6">-->
<!--        &lt;!&ndash; Auflagerbauweise &ndash;&gt;-->
<!--        <b-form-group label-for="supportBaseType">-->
<!--          <template slot="label">-->
<!--            {{ $t('edgeDistanceEC.supportBaseType') }}-->
<!--          </template>-->
<!--          <b-input-group>-->
<!--            <b-form-select-->
<!--              id="supportBaseType"-->
<!--              v-model="supportBaseType"-->
<!--              :options="supportBaseTypeSelectOptions"-->
<!--            >-->
<!--            </b-form-select>-->
<!--          </b-input-group>-->
<!--        </b-form-group>-->
<!--      </div>-->
<!--    </div>-->
<!--    <div class="row">-->
<!--      <div class="col-lg-6">-->
<!--        <h3>-->
<!--          a<sub>2</sub>-->
<!--          <b-button-->
<!--            id="popover-depth-supporting-a2"-->
<!--            variant="link"-->
<!--          >-->
<!--            <font-awesome-icon icon="question-circle" />-->
<!--          </b-button>-->
<!--          <b-popover-->
<!--            target="popover-depth-supporting-a2"-->
<!--            triggers="hover"-->
<!--            placement="top"-->
<!--          >-->
<!--            <p>{{ $t('edgeDistanceEC.distanceSupportingTooltip') }}</p>-->
<!--            &lt;!&ndash;
<p v-html="$t('general.tooltipAdditionalInfo')"></p>&ndash;&gt;-->
<!--          </b-popover>-->
<!--        </h3>-->
<!--        &lt;!&ndash; Bauweise stützende Bauteil &ndash;&gt;-->
<!--        <b-form-group label-for="supportingBearingType">-->
<!--          <template slot="label">-->
<!--            {{ $t('edgeDistanceEC.supportingBearingType') }}-->
<!--          </template>-->
<!--          <b-input-group>-->
<!--            <b-form-select-->
<!--              id="supportingBearingType"-->
<!--              v-model="supportingBearingType"-->
<!--              :options="supportingBearingTypeSelectOptions"-->
<!--            >-->
<!--            </b-form-select>-->
<!--          </b-input-group>-->
<!--        </b-form-group>-->
<!--      </div>-->
<!--      <div class="col-lg-6">-->
<!--        <h3>-->
<!--          &#916;-->
<!--          <span class="text-muted">a<sub>2</sub></span>-->
<!--          <b-button-->
<!--            id="popover-depth-max-supporting-a2"-->
<!--            variant="link"-->
<!--          >-->
<!--            <font-awesome-icon icon="question-circle" />-->
<!--          </b-button>-->
<!--          <b-popover-->
<!--            target="popover-depth-max-supporting-a2"-->
<!--            triggers="hover"-->
<!--            placement="top"-->
<!--          >-->
<!--            <p>{{ $t('edgeDistanceEC.depthMaxSupportingTooltip') }}</p>-->
<!--            &lt;!&ndash;
<p v-html="$t('general.tooltipAdditionalInfo')"></p>&ndash;&gt;-->
<!--          </b-popover>-->
<!--        </h3>-->
<!--        &lt;!&ndash; Art stützende Bauteil &ndash;&gt;-->
<!--        <b-form-group label-for="maxSupportingBearingType">-->
<!--          <template slot="label">-->
<!--            {{ $t('edgeDistanceEC.maxSupportingBearingType') }}-->
<!--          </template>-->
<!--          <b-input-group>-->
<!--            <b-form-select-->
<!--              id="supportingConstructionType"-->
<!--              v-model="maxSupportingBearingType"-->
<!--              :options="maxSupportingBearingTypeSelectOptions"-->
<!--            >-->
<!--            </b-form-select>-->
<!--          </b-input-group>-->
<!--        </b-form-group>-->
<!--      </div>-->
<!--    </div>-->
<!--    <h3>-->
<!--      a<sub>3</sub>-->
<!--      <b-button-->
<!--        id="popover-depth-supporting-a3"-->
<!--        variant="link"-->
<!--      >-->
<!--        <font-awesome-icon icon="question-circle" />-->
<!--      </b-button>-->
<!--      <b-popover-->
<!--        target="popover-depth-supporting-a3"-->
<!--        triggers="hover"-->
<!--        placement="top"-->
<!--      >-->
<!--        <p>{{ $t('edgeDistanceEC.distanceSupportedTooltip') }}</p>-->
<!--        &lt;!&ndash;
<p v-html="$t('general.tooltipAdditionalInfo')"></p>&ndash;&gt;-->
<!--      </b-popover>-->
<!--    </h3>-->
<!--    <div class="row">-->
<!--      <div class="col-lg-6">-->
<!--        &lt;!&ndash; Bewehrung stützende Bauteil &ndash;&gt;-->
<!--        <b-form-group label-for="supportedReinforcement">-->
<!--          <template slot="label">-->
<!--            {{ $t('edgeDistanceEC.supportedReinforcement') }}-->
<!--          </template>-->
<!--          <b-input-group>-->
<!--            <b-form-select-->
<!--              id="supportedReinforcement"-->
<!--              v-model="supportedReinforcement"-->
<!--              :options="supportedReinforcementSelectOptions"-->
<!--            >-->
<!--            </b-form-select>-->
<!--          </b-input-group>-->
<!--        </b-form-group>-->
<!--      </div>-->
<!--      <div class="col-lg-6">-->
<!--        &lt;!&ndash; Art des Lagers &ndash;&gt;-->
<!--        <b-form-group label-for="supportedBearingType">-->
<!--          <template slot="label">-->
<!--            {{ $t('edgeDistanceEC.supportedBearingType') }}-->
<!--          </template>-->
<!--          <b-input-group>-->
<!--            <b-form-select-->
<!--              id="supportedBearingType"-->
<!--              v-model="supportedBearingType"-->
<!--              :options="supportedBearingTypeSelectOptions"-->
<!--            >-->
<!--            </b-form-select>-->
<!--          </b-input-group>-->
<!--        </b-form-group>-->
<!--      </div>-->
<!--    </div>-->
<!--  </div>-->
</template>

<script>
// eslint-disable-next-line no-console
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'FixedBearingsFormEffects',
  components: {
  },
  // props: {
  //   propPower: {
  //     type: Number,
  //     default: 400,
  //   },
  //   propBearingWidth: {
  //     type: Number,
  //     default: 280,
  //   },
  //   propBearingLength: {
  //     type: Number,
  //     default: 400,
  //   },
  //   propBearingThickness: {
  //     type: Number,
  //     default: 20,
  //   },
  //   propHolesCount: {
  //     type: Number,
  //     default: 0,
  //   },
  //   propHolesDiameter: {
  //     type: Number,
  //     default: 20,
  //   },
  // },
  data() {
    return {
      bearingThicknessOptions: this.$dataOptionsHelper.getBearingThicknessOptions(),
      bearingThicknessSelectOptions:
        Object.values(this.$dataOptionsHelper.getBearingThicknessOptions()),
      // power: this.propPower,
      // bearingWidth: this.propBearingWidth,
      // bearingLength: this.propBearingLength,
      // bearingThickness: this.propBearingThickness,
      // holesCount: this.propHolesCount,
      // holesDiameter: this.propHolesDiameter,
      // concreteQuality: 'C12/15',
      // componentSpan: 6000,
      // componentLength: 5000,
      holesCountOptions: this.$dataOptionsHelper.getBearingHolesOptions(),
      concreteQualityOptions: this.$dataOptionsHelper.getConcreteQualityOptions(),
      concreteQualitySelectOptions:
        Object.values(this.$dataOptionsHelper.getConcreteQualityOptions()),
      // supportBaseType: 'blanket',
      // supportingBearingType: 'line',
      // supportedReinforcement: 'continuousBars',
      // supportedBearingType: 'line',
      supportedBearingTypeOptions:
        this.$dataOptionsHelper.getSupportedBearingTypeOptions(this.$i18n),
      supportedBearingTypeSelectOptions:
        Object.values(this.$dataOptionsHelper.getSupportedBearingTypeOptions(this.$i18n)),
      // maxSupportingBearingType: 'concreteSteel',
      maxSupportingBearingTypeOptions:
        this.$dataOptionsHelper.getMaxSupportingTypeOptions(),
      maxSupportingBearingTypeSelectOptions:
        Object.values(this.$dataOptionsHelper.getMaxSupportingTypeOptions()),
    };
  },
  // props: {
  //   propBearingWidth: {
  //     type: Number,
  //     default: 100,
  //   },
  //   propBearingLength: {
  //     type: Number,
  //     default: 150,
  //   },
  //   propBearingThickness: {
  //     type: Number,
  //     default: 20,
  //   },
  //   propPower: {
  //     type: Number,
  //     default: 100,
  //   },
  // },
  computed: {
    ...mapGetters([
      'compressiveStress',
      'areaNetto',
      'concretePressure',
      'bearingStrength',
      'bearingSqueeze',
      'depthParValue',
      'depthBase',
      'distanceSupporting',
      'maxDistanceSupporting',
      'sumDistanceSupporting',
      'distanceSupported',
      'maxDistanceSupported',
      'sumDistanceSupported',
      'edgeDistance',
    ]),
    ...mapState([
      'fixedBearingConcreteType',
      'fixedBearingSupportBaseType',
      'fixedBearingSupportingBearingType',
      'fixedBearingSupportedReinforcement',
      'fixedBearingSupportedBearingType',
      'fixedBearingMaxSupportingBearingType',
      'fixedBearingComponentLength',
      'fixedBearingComponentSpan',
    ]),
    a2Status() {
      return {
        'table-danger text-danger': this.warningsA2.status,
      };
    },
    warningsA2() {
      const error = {
        status: false,
        msg: [],
      };
      if (
        this.bearingSqueeze > 0.4
        && (
          this.fixedBearingSupportingBearingType === 'supportingBearingTypeOptionMasonryLine'
          || this.fixedBearingSupportingBearingType === 'supportingBearingTypeOptionMasonrySingle'
        )
      ) {
        error.status = true;
        error.msg.push(this.$i18n.t('edgeDistanceEC.warningA2ConcreteStone'));
      }
      return error;
    },
    a3Status() {
      return {
        'table-danger text-danger': this.warningsA3.status,
      };
    },
    warningsA3() {
      const error = {
        status: false,
        msg: [],
      };
      if (
        this.fixedBearingSupportedBearingType === 'single'
        && (
          this.fixedBearingSupportedReinforcement === 'straightBars'
          || this.fixedBearingSupportedReinforcement === 'verticalReinforcement'
        )
      ) {
        if (this.fixedBearingSupportedReinforcement === 'straightBars') {
          error.status = true;
          error.msg.push(this.$i18n.t('edgeDistanceEC.warningA3EndConcrete'));
        }
        if (this.fixedBearingSupportedReinforcement === 'verticalReinforcement') {
          error.status = true;
          error.msg.push(this.$i18n.t('edgeDistanceEC.warningA3EndConcreteRadius'));
        }
      }
      return error;
    },
    // supportingBearingTypeOptions() {
    //   return this.$dataOptionsHelper.getSupportingBearingTypeOptions(
    //     this.$i18n,
    //     this.bearingSqueeze,
    //   );
    // },
    // supportedReinforcementOptions() {
    //   return
    //   this.$dataOptionsHelper.getReinforcementOptions(this.$i18n, this.supportedBearingType);
    // },
    // supportBaseTypeSelectOptions() {
    //   return Object.values(
    //     this.$dataOptionsHelper.getSupportBaseTypeOptions(this.$i18n, this.bearingSqueeze),
    //   );
    // },
    // supportingBearingTypeSelectOptions() {
    //   return Object.values(this.$dataOptionsHelper.getSupportingBearingTypeOptions(
    //     this.$i18n,
    //     this.bearingSqueeze,
    //   ));
    // },
    // supportedReinforcementSelectOptions() {
    //   return Object.values(
    //     this.$dataOptionsHelper.getReinforcementOptions(this.$i18n, this.supportedBearingType),
    //   );
    // },
    // depthBase() {
    //   return this.supportBaseTypeOptions[this.supportBaseType].distance;
    // },
    // distanceSupporting() {
    //   return this.supportingBearingTypeOptions[this.supportingBearingType].distance;
    // },
    // maxDistanceSupporting() {
    //   return this.maxSupportingBearingTypeOptions[this.maxSupportingBearingType].distance;
    // },
    // sumDistanceSupporting() {
    //   return this.$outputHelper.convertToFloat(this.distanceSupporting, 1)
    //     + this.$outputHelper.convertToFloat(this.maxDistanceSupporting, 1);
    // },
    // distanceSupported() {
    //   return this.supportedReinforcementOptions[this.supportedReinforcement].distance;
    // },
    // maxDistanceSupported() {
    //   return this.$outputHelper.convertToFloat(this.componentLength, 2) / 2500;
    // },
    // sumDistanceSupported() {
    //   return this.$outputHelper.convertToFloat(this.distanceSupported, 1)
    //     + this.$outputHelper.convertToFloat(this.maxDistanceSupported, 1);
    // },
    // concretePressure() {
    //   return this.concreteQualityOptions[this.fixedBearingConcreteType].factorCube;
    // },
    // bearingSqueeze() {
    //   return this.$outputHelper.convertToFloat(this.compressiveStress, 1)
    //     / this.$outputHelper.convertToInteger(this.concretePressure);
    // },
    // compressiveStress() {
    //   return this.$calcHelper.getCompressiveStress(
    //     this.power,
    //     this.areaNetto,
    //   );
    // },
    // areaNetto() {
    //   return this.$calcHelper.getAreaNetto(
    //     this.bearingWidth,
    //     this.bearingLength,
    //     this.holesCount,
    //     this.holesDiameter,
    //   );
    // },
    // depthParValue() {
    //   return CalcToolsEdgeDistanceEC.getDepthParValue(
    //     this.depthBase,
    //     this.distanceSupporting,
    //     this.distanceSupported,
    //     this.maxDistanceSupporting,
    //     this.maxDistanceSupported,
    //   );
    // },
    // edgeDistance() {
    //   return CalcToolsEdgeDistanceEC.getEdgeDistance(
    //     this.distanceSupporting,
    //     this.distanceSupported,
    //     this.maxDistanceSupporting,
    //     this.maxDistanceSupported,
    //   );
    // },
  },
  methods: {
    openModal(type) {
      this.$store.commit('fixedBearingSelectedBearingType', type);
      this.$bvModal.show('conventionalModalOutput');
    },
  },
};
</script>

<style scoped lang="scss">
  .table-col-type {
    width: 25%;
  }
  .table-col-possibility {
    width: 25%;
  }
  .table-col-recommended {
    width: 25%;
  }
  .table-col-print {
    width: 25%;
  }
</style>
